/**
 * One purchase order: preview, issue, print, save PDF.
 *
 * NO "create purchase receipt" LINK HERE, and its absence is deliberate.
 *
 * The sales-order screen offers "Create delivery note", because that conversion
 * exists. This milestone implements Purchase Orders ONLY, so there is no receipt
 * service, no route, and no conversion. A greyed-out or soon-to-be-wired button
 * would be a promise the app cannot keep, and the supplier-facing question "did
 * these goods arrive?" has no honest answer until receiving exists.
 */
import { useState } from 'react';
import { flushSync } from 'react-dom';
import { FileDown, Printer, Send } from 'lucide-react';
import { A4_HEIGHT_PX, A4_WIDTH_PX, fitScale, renderPreview } from '@core/doc/preview';
import { printCapabilities, printDocument, saveDocumentPdf } from '@data/services/printService';
import { useIssueDocument } from '@features/documents/useDocuments';
import { toast } from '@ui/components/Toaster';
import { usePurchaseOrder } from './usePurchaseOrders';

const assets = new URL('assets/', document.baseURI).href;

export function PurchaseOrderDocumentScreen({ documentId }: { documentId: string }) {
  const query = usePurchaseOrder(documentId);
  const issueMutation = useIssueDocument();
  const capabilities = printCapabilities();
  const [busy, setBusy] = useState(false);
  const scale = fitScale(window.innerWidth * 0.64);

  if (query.isLoading) return <p className="p-4 text-sm text-slate-400">Loading purchase order…</p>;
  if (query.isError || !query.data) {
    return <p role="alert" className="p-4 text-sm text-red-300">Could not open this purchase order.</p>;
  }
  const { model, stored, business } = query.data;
  const html = renderPreview(model, business, { scale }, assets);

  async function runPrint() {
    setBusy(true);
    try {
      await printDocument(model, business, assets);
    } catch (error) {
      toast({
        title: 'Could not print purchase order',
        description: error instanceof Error ? error.message : 'Try again.',
        tone: 'danger',
      });
    } finally {
      flushSync(() => setBusy(false));
    }
  }

  async function runIssue() {
    setBusy(true);
    try {
      await issueMutation.mutateAsync(documentId);
      await query.refetch();
      toast({
        title: `Purchase order ${stored.docNumber} issued`,
        description:
          'No stock was received and no payable was created — goods arrive with a receipt.',
        tone: 'success',
      });
    } catch (error) {
      toast({
        title: 'Could not issue purchase order',
        description: error instanceof Error ? error.message : 'Try again.',
        tone: 'danger',
      });
    } finally {
      setBusy(false);
    }
  }

  async function runSavePdf() {
    setBusy(true);
    try {
      const result = await saveDocumentPdf(model, business, assets);
      if (result.saved) toast({ title: 'PDF saved', description: result.filePath, tone: 'success' });
    } catch (error) {
      toast({
        title: 'Could not save purchase order PDF',
        description: error instanceof Error ? error.message : 'Try again.',
        tone: 'danger',
      });
    } finally {
      setBusy(false);
    }
  }

  return <div className="flex flex-col items-center gap-3 p-4 text-slate-100">
    <header className="flex w-full max-w-4xl flex-wrap items-center justify-between gap-3">
      <div>
        <h1 className="font-semibold">Purchase Order {stored.docNumber}</h1>
        <p data-testid="purchase-order-status" className="text-xs text-slate-400">
          {stored.status === 'DRAFT' ? 'Draft' : 'Issued'} · no stock received, no supplier payable
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        {stored.status === 'DRAFT' && (
          <>
            <a className="rounded border border-slate-700 px-3 py-2 text-sm" href={`#/purchases/orders/${documentId}/edit`}>
              Edit draft
            </a>
            <button
              data-testid="issue-purchase-order"
              disabled={busy}
              onClick={() => void runIssue()}
              className="rounded bg-emerald-700 px-3 py-2 text-sm"
            >
              <Send className="mr-1 inline h-4 w-4" />Issue purchase order
            </button>
          </>
        )}
        <button disabled={busy} onClick={() => void runPrint()} className="rounded bg-brand-500 px-3 py-2 text-sm">
          <Printer className="mr-1 inline h-4 w-4" />Print / Reprint
        </button>
        {capabilities.silentPdf && (
          <button disabled={busy} onClick={() => void runSavePdf()} className="rounded border border-slate-700 px-3 py-2 text-sm">
            <FileDown className="mr-1 inline h-4 w-4" />Save PDF
          </button>
        )}
      </div>
    </header>
    <iframe
      title="Purchase order preview"
      data-testid="purchase-order-preview"
      srcDoc={html}
      style={{ width: A4_WIDTH_PX * scale, height: A4_HEIGHT_PX * scale, border: 0 }}
    />
    <p className="text-xs text-slate-500">
      Preview · goods are received in a later Purchase Receipt, which is what moves stock
    </p>
    <a className="text-sm text-brand-400 underline" href="#/purchases/orders">Back to purchase orders</a>
  </div>;
}